import { useQuery } from '@apollo/client'
import Box from '@mui/material/Box'
import AlertBadge from 'components/UI/AlertBadge/AlertBadge'
import Tooltip from '@mui/material/Tooltip'
import ResetFiltersIcon from 'components/UI/icons/ResetFiltersIcon'
import SourceNotesIcon from 'components/icons/SourceNotesIcon'
import COLORS from 'lib/colors'
import UploadIcon from '@mui/icons-material/FileUploadOutlined'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import DownloadingIcon from '@mui/icons-material/Downloading'
import ListAltIcon from '@mui/icons-material/ListAlt'
import CloseIcon from '@mui/icons-material/Close'
import { ExportStatus } from '../components/ExportStatusDialog'
import {
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  GridColDef,
  GridFilterModel,
  Toolbar,
  ToolbarButton,
} from '@mui/x-data-grid-premium'
import Stack from '@mui/material/Stack'
import { Divider, IconButton, Typography } from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import FilterListIcon from '@mui/icons-material/FilterList'
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined'
import LeadTableFilterBar, { getVisibleLeadTableFilters } from './LeadTableFilterBar'
import { LeadRow, LookupMap } from './leadTableTypes'
import { RawDataGridTableSettings } from 'components/DataGrid/dataGridTableSettingsAdapter'
import { MouseEvent, useCallback, useState } from 'react'
import { SavedReportMenu } from '../components/SavedReportMenu'
import { GET_SAVED_REPORTS_QUERY } from 'components/SavedReports/queries'
import { useCurrentUser } from 'lib/CurrentUserProvider'
import { InitializedRecord } from '@orbit/records'
import { SavedReport } from 'generated/graphql'
import { deepEqual } from 'lib/utils'
import { deserializeDataGridSavedReportConfig, serializeDataGridSavedReportConfig } from 'components/DataGridSavedReports/dataGridSavedReportConfig'


export type LeadTableToolbarProps = {
  onResetFilters: () => void
  onExport: () => void
  onImport: () => void
  refreshTable: () => void
  exportStatus: ExportStatus | null
  filterModel: GridFilterModel
  columns: GridColDef<LeadRow>[]
  onFilterModelChange: (model: GridFilterModel) => void
  permitUpload: boolean
  loading: boolean
  permitAiAnalysis: boolean
  clientIdToName: LookupMap
  clientCampaignIdToName: LookupMap
  vendorIdToName: LookupMap
  degreeProgramIdToName: LookupMap
  statuses: LookupMap
  dispositions: LookupMap
  tableSettings: RawDataGridTableSettings
  updateTableSettings: (
    patch: Partial<RawDataGridTableSettings> | ((current: RawDataGridTableSettings) => Partial<RawDataGridTableSettings>)
  ) => void
}

export default function LeadTableToolbar({
  onResetFilters,
  onExport,
  onImport,
  refreshTable,
  exportStatus,
  filterModel,
  columns,
  onFilterModelChange,
  permitUpload,
  loading,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  permitAiAnalysis,
  clientIdToName,
  clientCampaignIdToName,
  vendorIdToName,
  degreeProgramIdToName,
  statuses,
  dispositions,
  tableSettings,
  updateTableSettings,
}: LeadTableToolbarProps) {
  const { currentUser } = useCurrentUser()
  const [savedReportAnchorEl, setSavedReportAnchorEl] = useState<HTMLElement | null>(null)

  const { data, refetch } = useQuery(GET_SAVED_REPORTS_QUERY, {
    variables: {
      userId: (currentUser as InitializedRecord).id,
      reportKey: 'leads',
    },
  })

  const reports = [...(data?.savedReports ?? [])]

  const selectedReport = reports.find(report =>
    report.id === tableSettings.savedReportId
  ) ?? null

  const hasChanges = useCallback(() => {
    if(!selectedReport?.dataGridReportConfig) {
      return false
    }

    const currentConfig = serializeDataGridSavedReportConfig(tableSettings)
    const selectedConfig = serializeDataGridSavedReportConfig(
      deserializeDataGridSavedReportConfig(selectedReport.dataGridReportConfig)
    )

    return !deepEqual(currentConfig, selectedConfig)
  }, [selectedReport, tableSettings])

  const hasVisibleFilters = getVisibleLeadTableFilters(filterModel).length > 0

  const handleOpenSavedReports = useCallback((event: MouseEvent<HTMLElement>) => {
    setSavedReportAnchorEl(event.currentTarget)
  }, [])

  const handleCloseSavedReports = useCallback(() => {
    setSavedReportAnchorEl(null)
  }, [])

  const applySavedReport = useCallback((savedReport: SavedReport) => {
    if(!savedReport.dataGridReportConfig) {
      throw new Error('DataGrid saved report config is missing')
    }

    updateTableSettings({
      ...deserializeDataGridSavedReportConfig(savedReport.dataGridReportConfig),
      savedReportId: savedReport.id,
      savedReportName: savedReport.name,
    })
  }, [updateTableSettings])

  const revertToSelectedSavedReport = useCallback(() => {
    if(!selectedReport) {
      return
    }

    applySavedReport(selectedReport)
  }, [applySavedReport, selectedReport])

  const unselectCurrentSavedReport = useCallback(() => {
    updateTableSettings({
      savedReportId: null,
      savedReportName: null,
    })
  }, [updateTableSettings])

  const refetchReports = useCallback(() => {
    void refetch()
  }, [refetch])

  const reportName = selectedReport?.name || tableSettings.savedReportName

  const exportCsvIcon = (
    <Box sx={{ position: 'relative', width: 18, height: 18, display: 'block' }}>
      {!!exportStatus && (exportStatus.status === 'running' || exportStatus.status === 'queued') && (
        <Box sx={{ position: 'absolute', left: -5, top: -5, lineHeight: 0 }}>
          <Tooltip title='Click for Status Update'>
            <Box
              component='span'
              sx={{
                display: 'block',
                lineHeight: 0,
                transform: 'scale(0.75)',
                transformOrigin: 'top left',
              }}
            >
              <DownloadingIcon
                sx={{
                  color: COLORS.coastalWaters,
                  display: 'block',
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      )}

      {!!exportStatus && exportStatus.status === 'discarded' && (
        <AlertBadge
          tipTitle='Error'
          sx={{
            left: -5,
            top: -5,
            transform: 'scale(0.75)',
            transformOrigin: 'top left',
          }}
        />
      )}

      {!!exportStatus && exportStatus.status === 'succeeded' && (
        <AlertBadge
          variant='success'
          tipTitle='Success'
          sx={{
            left: -5,
            top: -5,
            transform: 'scale(0.75)',
            transformOrigin: 'top left',
          }}
        />
      )}

      <DownloadIcon
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          fontSize: 18,
        }}
      />
    </Box>
  )

  return (
    <Toolbar style={{ padding: 0, flex: 'unset', flexShrink: 0 }}>
      <Stack sx={{ width: '100%', flexShrink: 0 }}>
        <Stack
          direction='row'
          alignItems='center'
          gap={0}
          sx={{
            height: 55,
            minHeight: 55,
            flexShrink: 0,
            justifyContent: 'end',
            pr: 2,
            '& .MuiIconButton-root': {
              color: COLORS.slateGray,
            },
            '& svg': {
              fontSize: '18px',
            },
            '& .MuiIconButton-root + .MuiIconButton-root': {
              ml: 0,
            },
          }}
        >
          {reportName &&
            <Stack direction='row' alignItems='center' sx={{ flex: 1, minWidth: 0, pl: 3 }}>
              <Typography variant='h2' noWrap>{reportName}</Typography>

              <Tooltip title='Reset to original table settings'>
                <div>
                  <IconButton
                    aria-label='Reset to original table settings'
                    onClick={revertToSelectedSavedReport}
                    disabled={!hasChanges()}
                    sx={{
                      ml: 1,
                      height: 30,
                      width: 30,
                      '& .MuiSvgIcon-root': { color: COLORS.frenchBlue },
                      '&.Mui-disabled .MuiSvgIcon-root': { color: COLORS.mediumGray },
                    }}
                  >
                    <SourceNotesIcon sx={{ width: 24, height: 24 }} />
                  </IconButton>
                </div>
              </Tooltip>

              {selectedReport &&
                <Tooltip title='Unselect current saved report'>
                  <IconButton aria-label='Unselect current saved report' onClick={unselectCurrentSavedReport}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              }
            </Stack>
          }

          <Stack
            direction='row'
            alignItems='center'
            divider={
              <Divider
                orientation='vertical'
                flexItem
                sx={{
                  mx: 0.5,
                  borderColor: COLORS.mediumGray,
                  borderRightWidth: 1,
                }}
              />
            }
          >
            <Stack direction='row' fontSize='10px'>
              <Tooltip title='Columns'>
                <ColumnsPanelTrigger
                  render={
                    <ToolbarButton aria-label='Columns'>
                      <ViewWeekOutlinedIcon fontSize='inherit' />
                    </ToolbarButton>
                  }
                />
              </Tooltip>

              <Tooltip title='Filters'>
                <FilterPanelTrigger
                  render={
                    <ToolbarButton aria-label='Filters'>
                      <Box sx={{ position: 'relative', lineHeight: 0 }}>
                        <FilterListIcon />

                        {hasVisibleFilters && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -3,
                              right: -3,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: COLORS.frenchBlue,
                            }}
                          />
                        )}
                      </Box>
                    </ToolbarButton>
                  }
                />
              </Tooltip>

              <Tooltip title='Reset Filters'>
                <IconButton aria-label='Reset Filters' onClick={onResetFilters}>
                  <ResetFiltersIcon />
                </IconButton>
              </Tooltip>

              {/* <PivotPanelTrigger render={<ToolbarButton />} /> */}
              {/* <ChartsPanelTrigger render={<ToolbarButton />} /> */}
              {/* {permitAiAnalysis && <AiAssistantPanelTrigger render={<ToolbarButton />} />} */}
            </Stack>

            <Stack direction='row'>
              {permitUpload && (
                <Tooltip title='Import CSV'>
                  <IconButton aria-label='Import CSV' onClick={onImport}>
                    <UploadIcon />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title='Export CSV'>
                <IconButton aria-label='Export CSV' onClick={onExport}>
                  {exportCsvIcon}
                </IconButton>
              </Tooltip>

              <Tooltip title='Saved Reports'>
                <IconButton aria-label='Saved Reports' onClick={handleOpenSavedReports}>
                  <ListAltIcon />
                </IconButton>
              </Tooltip>

              <SavedReportMenu
                anchorEl={savedReportAnchorEl}
                onClose={handleCloseSavedReports}
                tableSettings={tableSettings}
                updateTableSettings={updateTableSettings}
                reports={reports}
                selectedReport={selectedReport}
                hasChanges={hasChanges}
                onSelectReport={applySavedReport}
                revertToSelectedSavedReport={revertToSelectedSavedReport}
                refetchReports={refetchReports}
              />
            </Stack>

            <Tooltip title='Refresh'>
              <span>
                <IconButton aria-label='Refresh' onClick={refreshTable} disabled={loading}>
                  <SyncIcon style={{ transform: 'rotate(45deg)' }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {hasVisibleFilters && (
          <Box sx={{ borderTop: `1px solid ${COLORS.mediumGray}` }}>
            <LeadTableFilterBar
              filterModel={filterModel}
              columns={columns}
              onFilterModelChange={onFilterModelChange}
              clientIdToName={clientIdToName}
              clientCampaignIdToName={clientCampaignIdToName}
              vendorIdToName={vendorIdToName}
              degreeProgramIdToName={degreeProgramIdToName}
              statuses={statuses}
              dispositions={dispositions}
            />
          </Box>
        )}
      </Stack>
    </Toolbar>
  )
}
